<template>
  <div
    v-bind="$attrs"
    v-click-outside="onClickOutside"
    v-on="$listeners"
    :disabled="disabled"
    :class="$style.root"
    :style="style.root"
  >
    <div
      ref="formPlaceholder"
      :style="style.formPlaceholder"
    />
    <div
      :disabled="disabled"
      :error="error"
      :expandable="expandable"
      :focus="focus"
      :class="$style.container"
      :style="style.container"
    >
      <div
        ref="form"
        :style="style.form"
      >
        <div
          :disabled="disabled"
          :error="error"
          :expandable="expandable"
          :focus="focus"
          :hover="formOnHover"
          @mouseenter="formOnHover = true"
          @mouseleave="formOnHover = false"
          :class="$style.left"
          :style="style.left"
        >
          <slot
            name="left"
            :disabled="disabled"
            :error="error"
            :expandable="expandable"
            :focus="focus"
          />
        </div>
        <div
          :disabled="disabled"
          :error="error"
          :expandable="expandable"
          :focus="focus"
          :hover="formOnHover"
          @mouseenter="formOnHover = true"
          @mouseleave="formOnHover = false"
          :class="$style.inputContainer"
          :style="style.inputContainer"
        >
          <slot
            name="input-left"
            :disabled="disabled"
            :error="error"
            :expandable="expandable"
            :focus="focus"
          />
          <input
            ref="input"
            v-model="input"
            :disabled="disabled"
            :error="error"
            :expandable="expandable"
            :focus="focus"
            :placeholder="placeholder"
            :type="inputType"
            @blur="() => { focus = false; }"
            @compositionstart="isComposing = true"
            @compositionend="onCompositionEnd"
            @focus="() => { focus = true; }"
            @input="onChangeInput"
            @keydown="onKeyDown"
            @keyup="onKeyUp"
            :class="$style.input"
            :style="style.input"
          />
          <slot
            name="input-right"
            :disabled="disabled"
            :error="error"
            :expandable="expandable"
            :focus="focus"
          />
        </div>
        <div
          :disabled="disabled"
          :error="error"
          :focus="focus"
          :expandable="expandable"
          :hover="formOnHover"
          @mouseenter="formOnHover = true"
          @mouseleave="formOnHover = false"
          :class="$style.right"
          :style="style.right"
        >
          <slot
            name="right"
            :disabled="disabled"
            :errorMessage="errorMessage"
            :expandable="expandable"
            :focus="focus"
          />
        </div>
      </div>
      <div
        v-if="!disabled && expandable"
        ref="dropdown"
        :class="$style.dropdown"
        :style="style.dropdown"
      >
        <div
          :class="$style.dropdownTop"
          :style="style.dropdownTop"
        >
          <slot
            name="dropdown-top"
            :input="input"
          />
        </div>
        <div :style="style.dropdownInnerContainer">
          <div :style="style.dropdownLeft">
            <div
              v-for="(option, index) in options"
              :hover="indexOnHover === index"
              :key="`${index}_right`"
              :selected="index === selectedIndex"
              @mouseenter="indexOnHover = index"
              @mouseleave="indexOnHover = null"
              :class="$style.row"
              :style="style.row">
              <div :style="style.option">
                <slot
                  name="option-left"
                  :index="index"
                  :input="input"
                  :label="option[optionLabelKey]"
                  :option="option"
                />
              </div>
            </div>
          </div>
          <div :style="style.dropdownCenter">
            <div
              v-for="(option, index) in options"
              :hover="indexOnHover === index"
              :key="index"
              :selected="index === selectedIndex"
              @mousedown="selectOption(option, index)"
              @mouseenter="indexOnHover = index"
              @mouseleave="indexOnHover = null"
              :class="$style.row"
              :style="style.row"
            >
              <div :style="style.option">
                <slot
                  name="option"
                  :index="index"
                  :input="input"
                  :label="option[optionLabelKey]"
                  :option="option"
                />
              </div>
            </div>
          </div>
          <div :style="style.dropdownRight">
            <div
              v-for="(option, index) in options"
              :hover="indexOnHover === index"
              :key="`${index}_right`"
              :selected="index === selectedIndex"
              @mouseenter="indexOnHover = index"
              @mouseleave="indexOnHover = null"
              :class="$style.row"
              :style="style.row"
            >
              <div :style="style.option">
                <slot
                  name="option-right"
                  :index="index"
                  :input="input"
                  :label="option[optionLabelKey]"
                  :option="option"
                />
              </div>
            </div>
          </div>
        </div>
        <div
          :class="$style.dropdownBottom"
          :style="style.dropdownBottom"
        >
          <slot
            name="dropdown-bottom"
            :input="input"
          />
        </div>
      </div>
      <div
        v-if="!expandable && error"
        :class="$style.error"
        :style="style.error"
      >
        <slot
          name="error"
          :errorMessage="errorMessage"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import ClickOutside from 'vue-click-outside'
  import { collapseWhitespace, toHalfWidth } from '@js/str-utils'
  import { CssStyleProcessor } from '@js/vue-style-utils'
  import { isArrowDown, isArrowUp, isBackspace, isEnter } from '@js/event-utils'

  export default {
    name: 'BaseInput',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change-input',
    },
    directives: {
      ClickOutside,
    },
    props: {
      vmodelValue: {
        type: String,
        required: true
      },
      clearCacheOnEnter: {
        type: Boolean,
        required: false,
        default: false,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      errorMessage: {
        type: String,
        required: false,
        default: ''
      },
      expandableFunction: {
        type: [Function, Object],
        required: false,
        default: null,
      },
      inputType: {
        type: String,
        required: false,
        default: '',
      },
      maxHeightDropdown: {
        type: String,
        required: false,
        default: 'none'
      },
      minWidthInput: {
        type: String,
        required: false,
        default: '120px',
      },
      optionLabelKey: {
        type: String,
        required: false,
        default: 'label'
      },
      options: {
        type: Array,
        required: false,
        default: () => []
      },
      placeholder: {
        type: String,
        required: false,
        default: ''
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {}
      },
      unfocusOnEnter: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    data: function () {
      return {
        formOnHover: false,
        focus: false,
        indexOnHover: null,
        input: this.vmodelValue,
        inputCache: this.vmodelValue,
        selectedIndex: null,
        zIndexDropdown: null,
        style: {
          container: {
            alignItems: 'flex-start',
            display: 'flex',
            flexDirection: 'column',
            left: 0,
            position: 'absolute',
            top: 0,
            width: '100%',
          },
          dropdownBottom: {
            width: '100%',
          },
          dropdownCenter: {
            height: '100%',
            flex: 1,
            //overflowX: 'scroll',
            //overflowY: 'visible',
          },
          dropdownInnerContainer: {
            height: '100%',
            display: 'flex',
            width: '100%',
          },
          dropdownLeft: {},
          dropdownRight: {},
          dropdown: {
            left: 0,
            maxHeight: this.maxHeightDropdown,
            overflowX: 'auto',
            overflowY: 'scroll',
            width: '100%',
          },
          dropdownTop: {
            width: '100%',
          },
          error: {
            left: 0,
            position: 'absolute',
          },
          form: {
            alignItems: 'flex-start',
            display: 'flex',
            width: '100%',
          },
          formPlaceholder: {
            width: '100%',
          },
          inputContainer: {
            alignItems: 'center',
            display: 'flex',
            height: 'fit-content',
            flex: 1,
          },
          /**
          * If style.input doesn't have width === '99%' and
          * form width is smaller, scroll bar in x-direction
          * appears even if input is empty.
          */
          input: {
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none',
            minWidth: this.minWidthInput,
            width: '99%',
          },
          left: {
            alignItems: 'center',
            boxSizing: 'content-box',
            display: 'flex',
            height: 'fit-content',
          },
          option: {},
          right: {
            alignItems: 'center',
            boxSizing: 'content-box',
            display: 'flex',
            height: 'fit-content',
          },
          root: {
            position: 'relative',
          },
          row: {},
        },
      };
    },
    created: function () {
      let proc = new CssStyleProcessor(
        '--v2-base-input',
        ['focus', 'error', 'expandable'],
      );

      Object.assign(
        this.style.container,
        proc.getExtendedStyle(this.styleConfig.container, 'container'),
      );
      Object.assign(
        this.style.inputContainer,
        proc.getExtendedStyle(
          this.styleConfig.inputContainer,
          'inputContainer',
        ),
      );
      Object.assign(
        this.style.left,
        proc.getExtendedStyle(this.styleConfig.left, 'left'),
      );
      Object.assign(
        this.style.right,
        proc.getExtendedStyle(this.styleConfig.right, 'right'),
      );
      Object.assign(
        this.style.root,
        proc.getExtendedStyle(this.styleConfig.root, 'root'),
      );

      proc = new CssStyleProcessor(
        '--v2-base-input',
        ['focus', 'error', 'expandable', 'placeholder'],
      );

      Object.assign(
        this.style.input,
        proc.getExtendedStyle(this.styleConfig.input, 'input'),
      );

      proc = new CssStyleProcessor('--v2-base-input', ['selected']);

      Object.assign(
        this.style.row,
        proc.getExtendedStyle(this.styleConfig.row, 'row'),
      );

      proc = new CssStyleProcessor('--v2-base-input');

      Object.assign(
        this.style.dropdown,
        proc.getExtendedStyle(this.styleConfig.dropdown, 'dropdown'),
      );
      Object.assign(
        this.style.dropdownBottom,
        proc.getExtendedStyle(this.styleConfig.dropdownBottom, 'dropdown-bottom'),
      );
      Object.assign(
        this.style.dropdownTop,
        proc.getExtendedStyle(this.styleConfig.dropdownTop, 'dropdown-top'),
      );
      Object.assign(
        this.style.error,
        proc.getExtendedStyle(this.styleConfig.error, 'error'),
      );

      Object.assign(
        this.style.dropdownCenter,
        this.styleConfig.dropdownCenter,
      );
      Object.assign(
        this.style.dropdownLeft,
        this.styleConfig.dropdownLeft,
      );
      Object.assign(
        this.style.dropdownRight,
        this.styleConfig.dropdownRight,
      );
      Object.assign(
        this.style.form,
        this.styleConfig.form,
      );
      Object.assign(
        this.style.formPlaceholder,
        this.styleConfig.formPlaceholder,
      );
      Object.assign(
        this.style.option,
        this.styleConfig.option,
      );
    },
    mounted: function () {
      this.focus = document.activeElement === this.$refs.input;
    },
    computed: {
      expandable: function () {
        if (this.expandableFunction === null) {
          return (
            this.errorMessage.length === 0 &&
            this.focus &&
            this.options.length > 0
          );
        } else {
          return this.expandableFunction({
            disabled: this.disabled,
            errorMessage: this.errorMessage,
            focus: this.focus,
            input: this.input,
            options: this.options,
          });
        }
      },
      error: function () {
        return this.errorMessage.length > 0;
      },
    },
    methods: {
      clearCacheForDropdown: function () {
        this.inputCache = '';
        this.selectedIndex = null;
      },
      initCacheForDropdown: function () {
        this.inputCache = this.input;
        this.selectedIndex = null;
      },
      onChangeInput: function (e) {
        this.input = collapseWhitespace(toHalfWidth(this.input)).trimStart();
        this.inputCache = e.target.value;
        this.selectedIndex = null;
        this.$emit('type-input', this.input);
      },
      onClickOutside: function (e) {
        this.$emit('click-outside', e);
      },
      onCompositionEnd() {
        this.isComposing = false;
      },
      onKeyDown: function (e) {
        if (this.disabled) {
          return;
        }

        if (isArrowDown(e)) {
          this.selectNextOption();
        } else if (isArrowUp(e)) {
          this.selectPreviousOption();
        } else if (isBackspace(e)) {
          if (this.isComposing) {
            e.preventDefault();
            return;
          }
          this.$emit('backspacedown', this.input);
        } else if (isEnter(e)) {
          if (this.isComposing) {
            e.preventDefault();
            return;
          }
          if (this.selectedIndex === undefined && this.selectedIndex === null) {
            this.selectOption(this.options[this.selectedIndex], this.selectedIndex);
          }
          if (this.unfocusOnEnter) {
            this.unfocusInput();
          }
          if (this.clearCacheOnEnter) {
            this.clearCacheForDropdown();
          }
          this.$emit('enterdown', this.input);
        }
      },
      onKeyUp: function (e) {
        if (this.disabled) {
          return;
        }

        if (isBackspace(e)) {
          this.$emit('backspaceup', this.input);
        }
      },
      selectNextOption: function () {
        if (this.options.length === 0) {
          this.selectedIndex = null;
        } else if (this.selectedIndex === null) {
          this.selectedIndex = 0;
        } else if (this.selectedIndex >= this.options.length - 1) {
          this.selectedIndex = null;
        } else {
          this.selectedIndex++;
        }

        this.updateInputByOption(
          this.options[this.selectedIndex], this.selectedIndex);
      },
      selectOption: function (option, index) {
        this.updateInputByOption(option, index);
      },
      selectPreviousOption: function () {
        if (this.options.length === 0 || this.selectedIndex === 0) {
          this.selectedIndex = null;
        } else if (this.selectedIndex === null) {
          this.selectedIndex = this.options.length - 1;
        } else if (this.selectedIndex <= 0) {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex--;
        }

        this.updateInputByOption(
          this.options[this.selectedIndex], this.selectedIndex);
      },
      unfocusInput: function () {
        this.$refs.input.blur();
        this.focus = false;
      },
      updateInputByOption: function (option, index) {
        const newInput = index === null
          ? this.inputCache
          : option[this.optionLabelKey];
        if (this.input !== newInput) {
          this.input = newInput;
        }
        this.$emit('navigate-option', { option, index });
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.input = newVal;
      },
      focus: function (newVal, oldVal) {
        if (!newVal) {
          this.initCacheForDropdown();
        }
        this.$emit(newVal ? 'focus' : 'unfocus');
      },
      input: function (newVal, oldVal) {
        this.$emit('change-input', newVal);
      },
    },
  };
</script>

<style lang="stylus" module>
  .container
    background-color var(--v2-base-input-container-background-color)
    border var(--v2-base-input-container-border)
    border-radius var(--v2-base-input-container-border-radius)
    border-width var(--v2-base-input-container-border-width)
    box-shadow var(--v2-base-input-container-box-shadow)
    &:hover
      background-color var(--v2-base-input-container-background-color-hover)
      border var(--v2-base-input-container-border-hover)
      border-radius var(--v2-base-input-container-border-radius-hover)
      border-width var(--v2-base-input-container-border-width-hover)
      box-shadow var(--v2-base-input-container-box-shadow-hover)
    &:active
      background-color var(--v2-base-input-container-background-color-active)
      border var(--v2-base-input-container-border-active)
      border-radius var(--v2-base-input-container-border-radius-active)
      border-width var(--v2-base-input-container-border-width-active)
      box-shadow var(--v2-base-input-container-box-shadow-active)
    &[focus]
      background-color var(--v2-base-input-container-background-color-focus)
      border var(--v2-base-input-container-border-focus)
      border-radius var(--v2-base-input-container-border-radius-focus)
      border-width var(--v2-base-input-container-border-width-focus)
      box-shadow var(--v2-base-input-container-box-shadow-focus)
    &[error]
      background-color var(--v2-base-input-container-background-color-error)
      border var(--v2-base-input-container-border-error)
      border-radius var(--v2-base-input-container-border-radius-error)
      border-width var(--v2-base-input-container-border-width-error)
      box-shadow var(--v2-base-input-container-box-shadow-error)
    &[expandable]
      background-color var(--v2-base-input-container-background-color-expandable)
      border var(--v2-base-input-container-border-expandable)
      border-radius var(--v2-base-input-container-border-radius-expandable)
      border-width var(--v2-base-input-container-border-width-expandable)
      box-shadow var(--v2-base-input-container-box-shadow-expandable)
    &[disabled]
      background-color var(--v2-base-input-container-background-color-disabled)
      border var(--v2-base-input-container-border-disabled)
      border-radius var(--v2-base-input-container-border-radius-disabled)
      border-width var(--v2-base-input-container-border-width-disabled)
      box-shadow var(--v2-base-input-container-box-shadow-disabled)

  .dropdown
    background-color var(--v2-base-input-dropdown-background-color)
    border var(--v2-base-input-dropdown-border)
    border-radius var(--v2-base-input-dropdown-border-radius)
    border-width var(--v2-base-input-dropdown-border-width)
    box-shadow var(--v2-base-input-dropdown-box-shadow)
    &:hover
      background-color var(--v2-base-input-dropdown-background-color-hover)
      border var(--v2-base-input-dropdown-border-hover)
      border-radius var(--v2-base-input-dropdown-border-radius-hover)
      border-width var(--v2-base-input-dropdown-border-width-hover)
      box-shadow var(--v2-base-input-dropdown-box-shadow-hover)

  .dropdownBottom
    background-color var(--v2-base-input-dropdown-bottom-background-color)
    border var(--v2-base-input-dropdown-bottom-border)
    border-radius var(--v2-base-input-dropdown-bottom-border-radius)
    border-width var(--v2-base-input-dropdown-bottom-border-width)
    box-shadow var(--v2-base-input-dropdown-bottom-box-shadow)
    &:hover
      background-color var(--v2-base-input-dropdown-bottom-background-color-hover)
      border var(--v2-base-input-dropdown-bottom-border-hover)
      border-radius var(--v2-base-input-dropdown-bottom-border-radius-hover)
      border-width var(--v2-base-input-dropdown-bottom-border-width-hover)
      box-shadow var(--v2-base-input-dropdown-bottom-box-shadow-hover)

  .dropdownTop
    background-color var(--v2-base-input-dropdown-top-background-color)
    border var(--v2-base-input-dropdown-top-border)
    border-radius var(--v2-base-input-dropdown-top-border-radius)
    border-width var(--v2-base-input-dropdown-top-border-width)
    box-shadow var(--v2-base-input-dropdown-top-box-shadow)
    &:hover
      background-color var(--v2-base-input-dropdown-top-background-color-hover)
      border var(--v2-base-input-dropdown-top-border-hover)
      border-radius var(--v2-base-input-dropdown-top-border-radius-hover)
      border-width var(--v2-base-input-dropdown-top-border-width-hover)
      box-shadow var(--v2-base-input-dropdown-top-box-shadow-hover)

  .error
    background-color var(--v2-base-input-error-background-color)
    border var(--v2-base-input-error-border)
    border-radius var(--v2-base-input-error-border-radius)
    border-width var(--v2-base-input-error-border-width)
    box-shadow var(--v2-base-input-error-box-shadow)
    color var(--v2-base-input-error-color)
    font-family var(--v2-base-input-error-font-family)
    font-size var(--v2-base-input-error-font-size)
    font-weight var(--v2-base-input-error-font-weight)
    text-decoration var(--v2-base-input-error-text-decoration)

  .input
    background-color transparent
    border none
    color var(--v2-base-input-input-color)
    cursor var(--v2-base-input-input-cursor)
    font-family var(--v2-base-input-input-font-family)
    font-size var(--v2-base-input-input-font-size)
    font-weight var(--v2-base-input-input-font-weight)
    text-decoration var(--v2-base-input-input-text-decoration)
    &:hover
      background-color transparent
      border none
      color var(--v2-base-input-input-color-hover)
      cursor var(--v2-base-input-input-cursor-hover)
      font-family var(--v2-base-input-input-font-family-hover)
      font-size var(--v2-base-input-input-font-size-hover)
      font-weight var(--v2-base-input-input-font-weight-hover)
      text-decoration var(--v2-base-input-input-text-decoration-hover)
    &[focus]
      background-color transparent
      border none
      color var(--v2-base-input-input-color-focus)
      cursor var(--v2-base-input-input-cursor-focus)
      font-family var(--v2-base-input-input-font-family-focus)
      font-size var(--v2-base-input-input-font-size-focus)
      font-weight var(--v2-base-input-input-font-weight-focus)
      text-decoration var(--v2-base-input-input-text-decoration-focus)
    &[error]
      background-color transparent
      border none
      color var(--v2-base-input-input-color-error)
      cursor var(--v2-base-input-input-cursor-error)
      font-family var(--v2-base-input-input-font-family-error)
      font-size var(--v2-base-input-input-font-size-error)
      font-weight var(--v2-base-input-input-font-weight-error)
      text-decoration var(--v2-base-input-input-text-decoration-error)
    &[expandable]
      background-color transparent
      border none
      color var(--v2-base-input-input-color-expandable)
      cursor var(--v2-base-input-input-cursor-expandable)
      font-family var(--v2-base-input-input-font-family-expandable)
      font-size var(--v2-base-input-input-font-size-expandable)
      font-weight var(--v2-base-input-input-font-weight-expandable)
      text-decoration var(--v2-base-input-input-text-decoration-expandable)
    &[disabled]
      background-color transparent
      border none
      color var(--v2-base-input-input-color-disabled)
      cursor var(--v2-base-input-input-cursor-disabled)
      font-family var(--v2-base-input-input-font-family-disabled)
      font-size var(--v2-base-input-input-font-size-disabled)
      font-weight var(--v2-base-input-input-font-weight-disabled)
      text-decoration var(--v2-base-input-input-text-decoration-disabled)
    &::placeholder
      background-color transparent
      border none
      color var(--v2-base-input-input-color-placeholder)
      cursor var(--v2-base-input-input-cursor-placeholder)
      font-family var(--v2-base-input-input-font-family-placeholder)
      font-size var(--v2-base-input-input-font-size-placeholder)
      font-weight var(--v2-base-input-input-font-weight-placeholder)
      text-decoration var(--v2-base-input-input-text-decoration-placeholder)

  .inputContainer
    background-color var(--v2-base-input-input-container-background-color)
    border var(--v2-base-input-input-container-border)
    border-radius var(--v2-base-input-input-container-border-radius)
    border-width var(--v2-base-input-input-container-border-width)
    box-shadow var(--v2-base-input-input-container-box-shadow)
    &:hover, &[hover]
      background-color var(--v2-base-input-input-container-background-color-hover)
      border var(--v2-base-input-input-container-border-hover)
      border-radius var(--v2-base-input-input-container-border-radius-hover)
      border-width var(--v2-base-input-input-container-border-width-hover)
      box-shadow var(--v2-base-input-input-container-box-shadow-hover)
    &:active
      background-color var(--v2-base-input-input-container-background-color-active)
      border var(--v2-base-input-input-container-border-active)
      border-radius var(--v2-base-input-input-container-border-radius-active)
      border-width var(--v2-base-input-input-container-border-width-active)
      box-shadow var(--v2-base-input-input-container-box-shadow-active)
    &[focus]
      background-color var(--v2-base-input-input-container-background-color-focus)
      border var(--v2-base-input-input-container-border-focus)
      border-radius var(--v2-base-input-input-container-border-radius-focus)
      border-width var(--v2-base-input-input-container-border-width-focus)
      box-shadow var(--v2-base-input-input-container-box-shadow-focus)
    &[error]
      background-color var(--v2-base-input-input-container-background-color-error)
      border var(--v2-base-input-input-container-border-error)
      border-radius var(--v2-base-input-input-container-border-radius-error)
      border-width var(--v2-base-input-input-container-border-width-error)
      box-shadow var(--v2-base-input-input-container-box-shadow-error)
    &[expandable]
      background-color var(--v2-base-input-input-container-background-color-expandable)
      border var(--v2-base-input-input-container-border-expandable)
      border-radius var(--v2-base-input-input-container-border-radius-expandable)
      border-width var(--v2-base-input-input-container-border-width-expandable)
      box-shadow var(--v2-base-input-input-container-box-shadow-expandable)
    &[disabled]
      background-color var(--v2-base-input-input-container-background-color-disabled)
      border var(--v2-base-input-input-container-border-disabled)
      border-radius var(--v2-base-input-input-container-border-radius-disabled)
      border-width var(--v2-base-input-input-container-border-width-disabled)
      box-shadow var(--v2-base-input-input-container-box-shadow-disabled)

  .left
    background-color var(--v2-base-input-left-background-color)
    border var(--v2-base-input-left-border)
    border-radius var(--v2-base-input-left-border-radius)
    border-width var(--v2-base-input-left-border-width)
    box-shadow var(--v2-base-input-left-box-shadow)
    &:hover, &[hover]
      background-color var(--v2-base-input-left-background-color-hover)
      border var(--v2-base-input-left-border-hover)
      border-radius var(--v2-base-input-left-border-radius-hover)
      border-width var(--v2-base-input-left-border-width-hover)
      box-shadow var(--v2-base-input-left-box-shadow-hover)
    &:active
      background-color var(--v2-base-input-left-background-color-active)
      border var(--v2-base-input-left-border-active)
      border-radius var(--v2-base-input-left-border-radius-active)
      border-width var(--v2-base-input-left-border-width-active)
      box-shadow var(--v2-base-input-left-box-shadow-active)
    &[focus]
      background-color var(--v2-base-input-left-background-color-focus)
      border var(--v2-base-input-left-border-focus)
      border-radius var(--v2-base-input-left-border-radius-focus)
      border-width var(--v2-base-input-left-border-width-focus)
      box-shadow var(--v2-base-input-left-box-shadow-focus)
    &[error]
      background-color var(--v2-base-input-left-background-color-error)
      border var(--v2-base-input-left-border-error)
      border-radius var(--v2-base-input-left-border-radius-error)
      border-width var(--v2-base-input-left-border-width-error)
      box-shadow var(--v2-base-input-left-box-shadow-error)
    &[expandable]
      background-color var(--v2-base-input-left-background-color-expandable)
      border var(--v2-base-input-left-border-expandable)
      border-radius var(--v2-base-input-left-border-radius-expandable)
      border-width var(--v2-base-input-left-border-width-expandable)
      box-shadow var(--v2-base-input-left-box-shadow-expandable)
    &[disabled]
      background-color var(--v2-base-input-left-background-color-disabled)
      border var(--v2-base-input-left-border-disabled)
      border-radius var(--v2-base-input-left-border-radius-disabled)
      border-width var(--v2-base-input-left-border-width-disabled)
      box-shadow var(--v2-base-input-left-box-shadow-disabled)

  .right
    background-color var(--v2-base-input-right-background-color)
    border var(--v2-base-input-right-border)
    border-radius var(--v2-base-input-right-border-radius)
    border-width var(--v2-base-input-right-border-width)
    box-shadow var(--v2-base-input-right-box-shadow)
    &:hover, &[hover]
      background-color var(--v2-base-input-right-background-color-hover)
      border var(--v2-base-input-right-border-hover)
      border-radius var(--v2-base-input-right-border-radius-hover)
      border-width var(--v2-base-input-right-border-width-hover)
      box-shadow var(--v2-base-input-right-box-shadow-hover)
    &:active
      background-color var(--v2-base-input-right-background-color-active)
      border var(--v2-base-input-right-border-active)
      border-radius var(--v2-base-input-right-border-radius-active)
      border-width var(--v2-base-input-right-border-width-active)
      box-shadow var(--v2-base-input-right-box-shadow-active)
    &[focus]
      background-color var(--v2-base-input-right-background-color-focus)
      border var(--v2-base-input-right-border-focus)
      border-radius var(--v2-base-input-right-border-radius-focus)
      border-width var(--v2-base-input-right-border-width-focus)
      box-shadow var(--v2-base-input-right-box-shadow-focus)
    &[error]
      background-color var(--v2-base-input-right-background-color-error)
      border var(--v2-base-input-right-border-error)
      border-radius var(--v2-base-input-right-border-radius-error)
      border-width var(--v2-base-input-right-border-width-error)
      box-shadow var(--v2-base-input-right-box-shadow-error)
    &[expandable]
      background-color var(--v2-base-input-right-background-color-expandable)
      border var(--v2-base-input-right-border-expandable)
      border-radius var(--v2-base-input-right-border-radius-expandable)
      border-width var(--v2-base-input-right-border-width-expandable)
      box-shadow var(--v2-base-input-right-box-shadow-expandable)
    &[disabled]
      background-color var(--v2-base-input-right-background-color-disabled)
      border var(--v2-base-input-right-border-disabled)
      border-radius var(--v2-base-input-right-border-radius-disabled)
      border-width var(--v2-base-input-right-border-width-disabled)
      box-shadow var(--v2-base-input-right-box-shadow-disabled)

  .root
    background-color var(--v2-base-input-root-background-color)
    border var(--v2-base-input-root-border)
    border-radius var(--v2-base-input-root-border-radius)
    border-width var(--v2-base-input-root-border-width)
    box-shadow var(--v2-base-input-root-box-shadow)
    filter var(--v2-base-input-root-filter)
    &:hover
      background-color var(--v2-base-input-root-background-color-hover)
      border var(--v2-base-input-root-border-hover)
      border-radius var(--v2-base-input-root-border-radius-hover)
      border-width var(--v2-base-input-root-border-width-hover)
      box-shadow var(--v2-base-input-root-box-shadow-hover)
      filter var(--v2-base-input-root-filter-hover)
    &[focus]
      background-color var(--v2-base-input-root-background-color-focus)
      border var(--v2-base-input-root-border-focus)
      border-radius var(--v2-base-input-root-border-radius-focus)
      border-width var(--v2-base-input-root-border-width-focus)
      box-shadow var(--v2-base-input-root-box-shadow-focus)
      filter var(--v2-base-input-root-filter-focus)
    &[disabled]
      background-color var(--v2-base-input-root-background-color-disabled)
      border var(--v2-base-input-root-border-disabled)
      border-radius var(--v2-base-input-root-border-radius-disabled)
      border-width var(--v2-base-input-root-border-width-disabled)
      box-shadow var(--v2-base-input-root-box-shadow-disabled)
      filter var(--v2-base-input-root-filter-disabled)

  .row
    background-color var(--v2-base-input-row-background-color)
    border var(--v2-base-input-row-border)
    border-radius var(--v2-base-input-row-border-radius)
    border-width var(--v2-base-input-row-border-width)
    box-shadow var(--v2-base-input-row-box-shadow)
    color var(--v2-base-input-row-color)
    cursor var(--v2-base-input-row-cursor)
    font-family var(--v2-base-input-row-font-family)
    font-size var(--v2-base-input-row-font-size)
    font-weight var(--v2-base-input-row-font-weight)
    text-decoration var(--v2-base-input-row-text-decoration)
    &[selected]
      background-color var(--v2-base-input-row-background-color-selected)
      border var(--v2-base-input-row-border-selected)
      border-radius var(--v2-base-input-row-border-radius-selected)
      border-width var(--v2-base-input-row-border-width-selected)
      box-shadow var(--v2-base-input-row-box-shadow-selected)
      color var(--v2-base-input-row-color-selected)
      cursor var(--v2-base-input-row-cursor-selected)
      font-family var(--v2-base-input-row-font-family-selected)
      font-size var(--v2-base-input-row-font-size-selected)
      font-weight var(--v2-base-input-row-font-weight-selected)
      text-decoration var(--v2-base-input-row-text-decoration-selected)
    &:hover, &[hover]
      background-color var(--v2-base-input-row-background-color-hover)
      border var(--v2-base-input-row-border-hover)
      border-radius var(--v2-base-input-row-border-radius-hover)
      border-width var(--v2-base-input-row-border-width-hover)
      box-shadow var(--v2-base-input-row-box-shadow-hover)
      color var(--v2-base-input-row-color-hover)
      cursor var(--v2-base-input-row-cursor-hover)
      font-family var(--v2-base-input-row-font-family-hover)
      font-size var(--v2-base-input-row-font-size-hover)
      font-weight var(--v2-base-input-row-font-weight-hover)
      text-decoration var(--v2-base-input-row-text-decoration-hover)
</style>
